import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api, session, unwrap } from '../api';
import analytics from '../services/analytics';

const AuthContext = createContext(null);

/** Query param on home that says the account behind the ended session has been deleted. */
export const ACCOUNT_GONE_PARAM = 'account_gone';
/**
 * bkfc-cms ends a deleted account's session with `401 SESSION_INVALID` plus
 * `reason: "account_deleted"` (docs/routes/auth.md), on refresh and on the bearer gate. Same code
 * as an ordinary expiry, so the session ends either way; the reason says which banner to show.
 */
const endedByDeletion = () => {
  const rejection = session.lastRejection?.();
  return rejection?.code === 'SESSION_INVALID' && rejection.reason === 'account_deleted';
};

const getCurrentPath = () => `${window.location.pathname}${window.location.search}`;

export const toLoginPath = returnTo => {
  const target = returnTo || getCurrentPath();
  return `/login?returnTo=${encodeURIComponent(target)}`;
};

/**
 * Signed in = the session holds an access token. Boot is one empty-body refresh against the
 * `bkfc_v1_rt` cookie; the session object, not this component, decides when a session ends.
 */
export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => session.getAccessToken() !== null);
  const [user, setUser] = useState(null);
  // `/v1/me`'s deletion block: `{state: 'none'}` or `{state: 'scheduled', undo_until, ...}`.
  const [deletion, setDeletion] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  // Set by logout so a protected page does not race the caller's redirect with its own
  // /login?returnTo=... one. The page navigates away; a later sign-in clears it.
  const [isSigningOut, setIsSigningOut] = useState(false);

  useEffect(
    () =>
      session.subscribe(() => {
        const signedIn = session.getAccessToken() !== null;
        setIsAuthenticated(signedIn);
        if (!signedIn) {
          setUser(null);
          setDeletion(null);
          if (endedByDeletion()) {
            setIsSigningOut(true); // no /login?returnTo= race with this navigation
            window.location.assign(`/?${ACCOUNT_GONE_PARAM}=1`);
          }
        }
      }),
    [],
  );

  // The session's account block has no names; /v1/me does, and it carries the deletion window.
  // Best effort: the menu falls back to the email until it lands.
  const loadMe = useCallback(
    isActive =>
      unwrap(api.GET('/v1/me'))
        .then(body => {
          if (isActive()) {
            setUser(body.data.account);
            setDeletion(body.data.deletion ?? null);
          }
        })
        .catch(() => {}),
    [],
  );

  useEffect(() => {
    let active = true;
    session.restore().then(token => {
      if (!active) {
        return;
      }
      analytics.setUserContext({ logged_in: token !== null });
      setIsLoading(false);
      if (token !== null) {
        loadMe(() => active);
      }
    });
    return () => {
      active = false;
    };
  }, [loadMe]);

  const signedIn = useCallback(
    account => {
      setUser(account);
      setIsSigningOut(false);
      analytics.setUserContext({ logged_in: true });
      loadMe(() => session.getAccessToken() !== null);
    },
    [loadMe],
  );

  const login = useCallback(
    async (email, password) => {
      const data = await session.login(email, password);
      signedIn(data.account);
      analytics.trackLogin('password');
      return data;
    },
    [signedIn],
  );

  /** @param {{email: string, password: string, consents: object[], first_name?: string, last_name?: string}} body */
  const register = useCallback(
    async body => {
      const data = await session.register(body);
      signedIn(data.account);
      analytics.trackSignUp('password');
      return data;
    },
    [signedIn],
  );

  const logout = useCallback(async () => {
    setIsSigningOut(true);
    await session.logout();
    analytics.trackLogout();
    analytics.setUserContext({ logged_in: false });
  }, []);

  const requestPasswordReset = useCallback(email => session.forgot(email).then(() => {}), []);

  const updatePassword = useCallback(
    ({ email, resetPasswordToken, newPassword }) =>
      session
        .reset({ email, reset_token: resetPasswordToken, new_password: newPassword })
        .then(() => {}),
    [],
  );

  const loginWithRedirect = useCallback(options => {
    const returnTo = options?.appState?.returnTo || getCurrentPath();
    window.location.assign(toLoginPath(returnTo));
  }, []);

  const value = useMemo(
    () => ({
      user,
      deletion,
      isAuthenticated,
      isLoading,
      isSigningOut,
      login,
      register,
      logout,
      requestPasswordReset,
      updatePassword,
      loginWithRedirect,
    }),
    [
      user,
      deletion,
      isAuthenticated,
      isLoading,
      isSigningOut,
      login,
      register,
      logout,
      requestPasswordReset,
      updatePassword,
      loginWithRedirect,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used inside AuthProvider');
  }
  return context;
};
